import { observable } from 'mobx'
import TagManager from 'react-gtm-module'
import isArray from 'lodash/isArray'
import omit from 'lodash/omit'
import {
  CommonContainer,
  customerContainer,
  addressContainer,
  accountsContainer,
  taxIDContainer,
  storeContainer,
} from 'src/models'
import {
  deleteFromLocalStorage,
  getLocalStorage,
  setLocalStorage,
  scrollState,
  trackAccountUpgrade,
  enablePhoneVerificationForConversion,
  enableLegalAddress,
  getLocaleCodeFromUrl,
  enableJITSU,
  isLATAM,
  enableLegalAddressForMemConversion,
  isDobEnabled,
} from 'src/utils'
import { toastState, modalState } from 'src/views/components'
import { i18nTranslate } from 'src/utils'
import { dateFormatDeps } from 'src/deps'
import {
  APPConfig,
  convertToBoolean,
  deleteFromSessionStorage,
} from 'config/appConfig'
import dayjs from 'dayjs'
import { pageNames } from 'src/routes/pathParams'
import { alertMessageKeys } from 'src/views/components/CartBlock/fixture'
import { getFraudDetectionProperties } from 'src/utils/signUpUtils'
import {
  getAddressFieldsConfig,
  isUserTypeEligibleForAddressFields,
} from 'src/utils/addressFieldsUtils'

/**
 * Builds the conversion `account.properties` list from the target account
 * type's attribute schema (e.g. licenseExpiration/licenseType/licenseNumber/
 * certificateAuthority, market-specific fields like PecEmail) instead of only
 * sponsorId - those attributes were previously dropped from the upgrade
 * payload because this schema was never merged into the properties list.
 *
 * @param {Array} baseProperties - properties always sent (currently [sponsorIdAttribute])
 * @param {{value?: string}} taxIdAttribute - resolved taxType attribute for this conversion
 * @param {string} taxNumberUnformatted - resolved taxNumberUnformatted value for this conversion
 * @returns {Array<{attributeId: string, value: string}>}
 */
const buildConversionAccountProperties = (
  baseProperties,
  taxIdAttribute,
  taxNumberUnformatted
) => {
  const computedValueByAttributeId = {
    taxType: taxIdAttribute?.value || '',
    taxNumberUnformatted: taxNumberUnformatted || '',
  }
  const legalTaxAccountProperties =
    accountsContainer?.legalTaxLoadParam?.postData?.accountProperties || []
  const profileProperties = accountsContainer?.profileResponse?.properties || []
  const attributeSchema =
    accountsContainer?.accountAttributesWithoutSize?.attributes || []

  const resolveValue = attributeId => {
    if (attributeId in computedValueByAttributeId) {
      return computedValueByAttributeId[attributeId]
    }
    const legalTaxMatch = legalTaxAccountProperties.find(
      property => property.attributeId === attributeId
    )
    if (legalTaxMatch) return legalTaxMatch.value || ''
    const profileMatch = profileProperties.find(
      property => property.attributeId === attributeId
    )
    return profileMatch?.value || ''
  }

  const schemaProperties = attributeSchema
    .filter(
      attribute =>
        attribute.required === true &&
        attribute.status?.toLowerCase() === 'active' &&
        attribute.identifier !== 'sponsorId'
    )
    .map(attribute => ({
      attributeId: attribute.identifier,
      value: resolveValue(attribute.identifier),
    }))

  const mergedAttributeIds = new Set(
    schemaProperties.map(property => property.attributeId)
  )
  const extraLegalTaxProperties = legalTaxAccountProperties.filter(
    property => !mergedAttributeIds.has(property.attributeId)
  )

  return [...baseProperties, ...schemaProperties, ...extraLegalTaxProperties]
}

/**
 * AccountsAddressContainer class extends CommonContainer.
 * This is likely the container class for managing account addresses.
 */
class AccountsAddressContainer extends CommonContainer {
  @observable addressData = []
  @observable pageProfileRedirect = false
  @observable currentAddressFormValues = {}
  @observable isAccountAddressEmpty = false
  @observable isConvertAccountAddressEmpty = false
  @observable currentConvertAddressFormValues = {}
  isFirstAddress = false
  consent = {}

  /**
   * Fetches all addresses associated with the logged in customer's account.
   *
   * Makes API call to get all addresses for the account.
   * Sets addressData to the response array.
   * Sets isFirstAddress based on whether addressData is empty.
   */
  getAllAccountAddress = async () => {
    const accountId = customerContainer?.accountId || ''
    const loadParams = {
      endPointName: 'getAllAccountAddress',
      pathParams: `${accountId}/addresses`,
    }
    this.addressData = await this.fetchResponse(loadParams)
    this.isFirstAddress =
      isArray(this.addressData) && this.addressData.length === 0
  }

  /**
   * Transforms address data into the format expected by the API.
   *
   * Takes in an address object and returns a transformed version with
   * keys renamed and values processed as needed for the API call.
   *
   * Handles splitting street lines, converting state to abbreviation,
   * lowercasing email, and adding fraud detection properties if enabled.
   */
  transformAddressData = async (address = {}) => {
    const {
      addressLine1,
      addressLine2,
      addressLine3,
      addressUserName,
      stateList,
      zip,
      postalCode,
      email,
      ...remainingProps
    } = address
    const data = {
      firstName: addressUserName,
      email: email?.toLowerCase(),
      street1: addressLine1,
      street2: addressLine2,
      street3: addressLine3,
      state: stateList,
      type: 'Local',
      zipCode: zip || postalCode,
      ...remainingProps,
    }
    const accertifyProps = (await getFraudDetectionProperties()) || {}
    const profileFraudCheckEnabled =
      APPConfig?.getAppConfig()?.ProfileFraudCheckEnabled === 'true' || false
    if (profileFraudCheckEnabled) {
      data.additionalProperties = accertifyProps || {}
    }
    return data
  }

  /**
   * Adds a new address for the logged in account.
   *
   * Takes in address info, calls API to add it, displays success/error toast messages,
   * and refetches all addresses for account on success.
   *
   * @param {Object} addressInfo - Address details
   * @param {boolean} showToastMessage - Whether to show toast on success
   */
  addAddress = async (addressInfo, showToastMessage = false) => {
    delete addressInfo.submitButton
    delete addressInfo.type

    const {
      setDefaultAddress = false,
      isDefault = false,
      ...remainingProps
    } = addressInfo
    remainingProps.default = setDefaultAddress || isDefault
    const postData = (await this.transformAddressData(remainingProps)) || {}
    const accountId = customerContainer.accountId
    const loadParams = {
      endPointName: 'addAccountAddress',
      pathParams: `${accountId}/addresses`,
      postData,
    }
    const response = await this.fetchResponse(loadParams)
    if (this.isSuccessResponse(response)) {
      showToastMessage &&
        toastState.setToastMessage(
          i18nTranslate(
            'accounts.addAddressSuccess',
            'Your address has been added.'
          ),
          true
        )
      await this.getAllAccountAddress(accountId)
    } else {
      toastState.setToastMessage(
        i18nTranslate(
          'accounts.addAddressFailure',
          'Sorry, we are unable to add your address. Please try again.'
        ),
        false
      )
    }
    return response
  }

  /**
   * Removes an address for the logged in account.
   *
   * Takes in address ID to remove, calls API to remove it,
   * displays success/error toast message, and refetches addresses on success.
   *
   * @param {Object} postData - Request body with address ID to remove
   */
  removeAddress = async postData => {}

  /**
   * Validates an updated address for the logged in account.
   *
   * Takes in updated address form data and existing address info.
   * Calls API to validate updated address.
   * On success, updates address via updateAccountAddress() and returns response.
   * On failure, displays error toast message and returns response.
   */
  validateUpdatedAddress = async (formdata, addressInfo) => {
    let addressAddResponse = {}
    let response = await addressContainer.originalAddressApiCall(formdata)
    if (this.isSuccessResponse(response)) {
      addressAddResponse = this.updateAccountAddress(formdata, addressInfo)
      return addressAddResponse
    } else {
      if (response.message === 'input.validation.failed') {
        response.message = alertMessageKeys(
          'Please correct the address to proceed.'
        )
      }
      toastState.setToastMessage(response.message, false)
      return response
    }
  }

  /**
   * Validates an original address submitted for the logged in account.
   *
   * Takes in address form data and existing address info.
   * Calls API to validate address.
   * On success, adds address via addAddress() and returns response.
   * On failure, displays error toast message and returns response.
   */
  validateOriginalAccountAddress = async (formdata, addressInfo) => {
    let addressAddResponse = {}
    let response = await addressContainer?.originalAddressApiCall(formdata)
    if (this.isSuccessResponse(response)) {
      addressAddResponse = this.addAddress(formdata, addressInfo)
      return addressAddResponse
    } else {
      if (response?.message === 'input.validation.failed') {
        response.message = alertMessageKeys(
          'Please correct the address to proceed.'
        )
      }
      toastState.setToastMessage(response?.message, false)
      return response
    }
  }

  /**
   * Updates the address for the given account ID by calling the API.
   *
   * Takes the address info object and a flag indicating if the address was updated.
   * Gets the address ID from the info object.
   * Transforms the address data and deletes the addressId property.
   * Calls the API endpoint with path params for account ID and address ID.
   * On success, shows a success toast and refreshes account data.
   * On failure, shows an error toast.
   */
  updateAccountAddress = async (addressInfo, isAddressUpadated = true) => {
    const addressId = addressInfo?.addressId || addressInfo.id
    const postData = (await this.transformAddressData(addressInfo)) || {}
    delete postData?.addressId
    const loadParams = {
      endPointName: 'updateAccountAddress',
      pathParams: `${customerContainer.accountId}/addresses/${addressId}`,
      postData,
    }
    const response = await this.fetchResponse(loadParams)
    if (this.isSuccessResponse(response)) {
      isAddressUpadated &&
        toastState.setToastMessage(
          i18nTranslate(
            'accounts.updateAddressSuccess',
            'Your address has been updated.'
          ),
          true
        )
      await this.getAllAccountAddress(customerContainer.accountId)
      await accountsContainer.getProfile(customerContainer.accountId)
    } else {
      toastState.setToastMessage(
        i18nTranslate(
          'accounts.updateAddressFailure',
          'Sorry, we are unable to update your address. Please try again.'
        ),
        false
      )
    }
    return response
  }

  /**
   * Updates the address for an account.
   *
   * Takes the form data and existing address info.
   * Extracts the setDefaultAddress flag into its own variable.
   * Sets the default field in the remaining form data based on the flag.
   * Gets the address ID from the existing address info.
   * Calls updateAccountAddress() to update the address via API with the combined info.
   */
  updateAddress = (formData, addressInfo) => {
    delete formData.submitButton
    const {
      firstName,
      lastName,
      setDefaultAddress = false,
      ...remainingProps
    } = formData

    remainingProps.default = setDefaultAddress
    remainingProps.firstName = firstName?.trim()
    remainingProps.lastName = lastName?.trim()
    const { addressId, id } = addressInfo
    this.updateAccountAddress({ ...remainingProps, id: addressId || id })
  }

  /**
   * Updates the default address for an account.
   *
   * Takes the address info object.
   * Creates a new object with the default property inverted.
   * Calls updateAccountAddress() with the new default address object.
   */
  updateDefaultAddress = addressInfo => {
    const defaultAddress = { ...addressInfo, default: !addressInfo.default }
    this.updateAccountAddress(defaultAddress)
  }

  /**
   * Converts a user account type string to an abbreviated code.
   *
   * @param {string} value - The account type string
   * @returns {string} The abbreviated account type code
   */
  getConversionType = value => {
    if (value === 'Retail Customer') {
      return 'RC'
    } else if (value === 'Preferred Customer/Member') {
      return 'PM'
    } else if (value === 'Brand Affiliate') {
      return 'BA'
    } else {
      return 'BE'
    }
  }

  /**
   * Fetches user consent data from the API.
   *
   * Calls fetchResponse() with the 'userConsent' endpoint.
   * Saves the response to this.consent.
   * Returns the API response.
   */
  getUserConsent = async () => {
    const loadParams = {
      endPointName: 'userConsent',
    }

    const response = await this.fetchResponse(loadParams)
    this.consent = response
    return response
  }
  /**
   * Updates the account type with the provided form data and profile data.
   * @param {Object} formData - The form data.
   * @param {Object} profileData - The profile data.
   * @returns {Promise} - A promise that resolves when the account type is updated.
   */
  updateAccountType = async (formData, profileData) => {
    const isTaxIdEnabled = convertToBoolean(
      APPConfig?.getAppConfig()?.enableLegalTaxId
    )
    const addressInfo = formData
    const { taxIdOptions } = formData
    const { consents } = await customerContainer.signupConsents()

    const addressId = addressInfo?.addressId || addressInfo.id
    const postData = (await this.transformAddressData(addressInfo)) || {}
    const {
      firstName = '',
      lastName = '',
      addressLine1 = '',
      addressLine2 = '',
      city = '',
      county = '',
      state = '',
      zip = '',
      country = '',
    } = addressInfo || ''

    // const { sponsorIDConvert } = profileData
    const accProperties = accountsContainer?.profileResponse?.properties || {}
    const spId =
      accProperties?.length > 0 &&
      accProperties?.find(list => list?.attributeId === 'sponsorId')

    const sponsorIDValue = spId?.value || ''
    let dateOfBirth = ''
    const conversionTargetType = profileData?.accountType || ''
    const dobSource = isDobEnabled(conversionTargetType)
      ? accountsContainer.conversionDateOfBirth
      : profileData?.dateOfBirth
    if (dobSource) {
      const formattedDate = dateFormatDeps?.getNormalFormattedDate(dobSource)
      if (formattedDate && formattedDate !== 'NaN-NaN-NaN') {
        dateOfBirth = dayjs(formattedDate).format('YYYY-MM-DD')
      }
    }
    dateOfBirth = dateOfBirth ? dayjs(dateOfBirth).format('YYYY-MM-DD') : ''
    const { createdBy, type1 } =
      accountsContainer?.profileResponse?.addresses?.[0] || ''
    const legalTaxName = accountsContainer?.legalTaxLoadParam?.postData?.name
    let legalTaxEntrepreneurName = ''
    if (isTaxIdEnabled && typeof legalTaxName === 'string') {
      legalTaxEntrepreneurName = legalTaxName.trim()
    }
    const businessName =
      conversionTargetType === 'Brand Affiliate - Business Entity'
        ? profileData?.bEFirstName || formData?.bEFirstName
        : ''
    const userName =
      legalTaxEntrepreneurName ||
      businessName ||
      customerContainer?.profileResponse?.accounts?.name ||
      ''
    const { default1 } =
      accountsContainer?.profileResponse?.addresses?.[0] || true
    const { validated } =
      accountsContainer?.profileResponse?.addresses?.[0] || false
    const id = customerContainer.accountId
    const type =
      this.getConversionType(customerContainer.type) +
      '_2_' +
      this.getConversionType(profileData.accountType)
    const { accountTypes } = accountsContainer?.nexGenAccountTypes
    let acList =
      accountTypes &&
      accountTypes.find(list => list.name === profileData.accountType)
    const accountIdConvert = acList?.id || ''

    const isToEnableIronClad =
      APPConfig?.getAppConfig()?.FEATURE_IRONCLAD_IMPLEMENTATION === 'true' ||
      false

    if (
      isToEnableIronClad ||
      (profileData?.refundAndPrivacyBrand && profileData?.termsOfUseBrand) ||
      (profileData?.refundAndPrivacy && profileData?.termsOfUse)
    ) {
      const consentInfo_1 = consents.find(
        consent =>
          consent.title === 'I acknowledge I have read Sales performance'
      )
      const consentInfo_2 = consents.find(
        consent =>
          consent.title === 'I have Read, Understand, Agree to BA agreement'
      )
      const consentInfo_3 = consents.find(
        consent => consent.title === 'PRIVACY_NOTICE'
      )
      const consentInfo_4 = consents.find(
        consent => consent.title === 'REFUND_POLICY'
      )

      const consentInfo_5 = consents.find(
        consent => consent.title === 'SALES_COMPENSATION_SUMMARY'
      )
      const consentInfo_6 = consents.find(
        consent => consent.title === 'POLICY_PROCEDURES'
      )
      const consentInfo_7 = consents.find(
        consent => consent.title === 'Terms & Conditions'
      )
      let userConsent = []

      const IronCladGroupId = accountsContainer?.IronCladGroupId
      const otherInfo = {
        browser: 'chrome',
        device: 'mobile',
        geoLocation:
          storeContainer.currentRegion ||
          getLocalStorage('currentRegion') ||
          '',
        ipAddress: this.consent.IPv4,
        id: IronCladGroupId,
      }
      const acceptanceInfoPage1 = {
        ...otherInfo,
        title: 'contracts_ironclad_page1',
        acceptanceData: accountsContainer?.acceptanceContractData1 || [],
      }
      const acceptanceInfoPage2 = {
        ...otherInfo,
        title: 'contracts_ironclad_page2',
        acceptanceData: accountsContainer?.acceptanceContractData2 || [],
      }
      const acceptanceInfoPage3 = {
        ...otherInfo,
        title: 'contracts_ironclad_page3',
        acceptanceData: accountsContainer?.acceptanceContractData3 || [],
      }

      if (
        consentInfo_1 &&
        consentInfo_2 &&
        consentInfo_3 &&
        !isToEnableIronClad
      ) {
        userConsent = [
          {
            id: consentInfo_1.id,
            consentId: consentInfo_1.id,
            browser: 'chrome',
            device: 'mobile',
            geoLocation:
              storeContainer.currentRegion ||
              getLocalStorage('currentRegion') ||
              '',
            ipAddress: this.consent.IPv4,
            createdTimestamp: new Date(),
            updatedTimestamp: new Date(),
          },
          {
            id: consentInfo_2.id,
            consentId: consentInfo_2.id,
            browser: 'chrome',
            device: 'mobile',
            geoLocation:
              storeContainer.currentRegion ||
              getLocalStorage('currentRegion') ||
              '',
            ipAddress: this.consent.IPv4,
            createdTimestamp: new Date(),
            updatedTimestamp: new Date(),
          },
          {
            id: consentInfo_3.id,
            consentId: consentInfo_3.id,
            browser: 'chrome',
            device: 'mobile',
            geoLocation:
              storeContainer.currentRegion ||
              getLocalStorage('currentRegion') ||
              '',
            ipAddress: this.consent.IPv4,
            createdTimestamp: new Date(),
            updatedTimestamp: new Date(),
          },
          {
            id: consentInfo_4.id,
            consentId: consentInfo_4.id,
            browser: 'chrome',
            device: 'mobile',
            geoLocation:
              storeContainer.currentRegion ||
              getLocalStorage('currentRegion') ||
              '',
            ipAddress: this.consent.IPv4,
            createdTimestamp: new Date(),
            updatedTimestamp: new Date(),
          },
          {
            id: consentInfo_5.id,
            consentId: consentInfo_5.id,
            browser: 'chrome',
            device: 'mobile',
            geoLocation:
              storeContainer.currentRegion ||
              getLocalStorage('currentRegion') ||
              '',
            ipAddress: this.consent.IPv4,
            createdTimestamp: new Date(),
            updatedTimestamp: new Date(),
          },
          {
            id: consentInfo_6.id,
            consentId: consentInfo_6.id,
            browser: 'chrome',
            device: 'mobile',
            geoLocation:
              storeContainer.currentRegion ||
              getLocalStorage('currentRegion') ||
              '',
            ipAddress: this.consent.IPv4,
            createdTimestamp: new Date(),
            updatedTimestamp: new Date(),
          },
          {
            id: consentInfo_7.id,
            consentId: consentInfo_7.id,
            browser: 'chrome',
            device: 'mobile',
            geoLocation:
              storeContainer.currentRegion ||
              getLocalStorage('currentRegion') ||
              '',
            ipAddress: this.consent.IPv4,
            createdTimestamp: new Date(),
            updatedTimestamp: new Date(),
          },
        ]
      }
      isToEnableIronClad &&
      accountsContainer?.convertAccoutType != 'Preferred Customer/Member'
        ? userConsent.push(
            acceptanceInfoPage1,
            acceptanceInfoPage2,
            acceptanceInfoPage3
          )
        : isToEnableIronClad
          ? userConsent.push(acceptanceInfoPage1)
          : ''

      const locale = APPConfig?.getActiveAppConfig()?.defaultLocale //en_US
      const language = locale?.split('_')?.pop().toLowerCase() // us
      let email, phone, tokenExTaxData, dupCharTokenExTaxData
      let customer = {}
      const sponsorIdAttribute = {
        attributeId: 'sponsorId',
        value: sponsorIDValue,
      }

      let taxIdAttribute = {
        attributeId: 'taxType',
        value:
          type === 'RC_2_BA' || type === 'PM_2_BA'
            ? language === 'us'
              ? 'Social Security Number'
              : 'Social Insurance Number'
            : language === 'us'
              ? 'Employer Identification Number'
              : taxIdOptions || profileData?.taxIdOptions,
      }
      let localCountry = storeContainer.storeIDValue === 'Canada' ? 'CA' : 'US'
      let addressType =
        localCountry === 'US' || localCountry === 'CA' ? 'Local' : 'HOME'
      if (type === 'RC_2_BA' || type === 'PM_2_BA') {
        if (
          taxIDContainer &&
          taxIDContainer?.taxTokenExData?.token !== undefined &&
          taxIDContainer?.taxTokenExData?.success === 'true'
        ) {
          tokenExTaxData = taxIDContainer?.taxTokenExData?.token
          dupCharTokenExTaxData = taxIDContainer?.taxTokenExDupCharData?.token
        }
        email = addressInfo?.email
        phone = addressInfo?.phone
        if (isDobEnabled(conversionTargetType)) {
          customer = {
            dateOfBirth,
          }
        }
      } else if (type === 'BA_2_BE') {
        if (
          taxIDContainer &&
          taxIDContainer?.taxTokenExData?.token !== undefined &&
          taxIDContainer?.taxTokenExData?.success === 'true'
        ) {
          tokenExTaxData = taxIDContainer?.taxTokenExData?.token
          dupCharTokenExTaxData = taxIDContainer?.taxTokenExDupCharData?.token
        }
        email = addressInfo?.businessEmail
        phone =
          addressInfo?.businessPhoneNumber || profileData?.businessPhoneNumber
        if (isDobEnabled(conversionTargetType)) {
          customer = {
            dateOfBirth,
          }
        }
      } else if (type === 'RC_2_PM') {
        phone = profileData?.phone
        if (
          isDobEnabled(conversionTargetType) &&
          dateOfBirth &&
          dateOfBirth.trim() !== ''
        ) {
          customer = {
            dateOfBirth,
          }
        }
      }
      const taxNumUnformatted = {
        attributeId: 'taxNumberUnformatted',
        value: dupCharTokenExTaxData,
      }
      const accertifyProps = (await getFraudDetectionProperties()) || {}
      const profileFraudCheckEnabled =
        APPConfig?.getAppConfig()?.ProfileFraudCheckEnabled === 'true' || false
      if (profileFraudCheckEnabled) {
        customer.customProperties = accertifyProps || {}
      }

      const { fields: citizenshipFieldKeys } = getAddressFieldsConfig()
      const citizenshipCustomProperties = isUserTypeEligibleForAddressFields(
        conversionTargetType
      )
        ? Object.fromEntries(
            citizenshipFieldKeys
              .filter(fieldKey => addressInfo?.[fieldKey])
              .map(fieldKey => [fieldKey, addressInfo[fieldKey]])
          )
        : {}
      if (Object.keys(citizenshipCustomProperties).length > 0) {
        customer.customProperties = {
          ...customer.customProperties,
          ...citizenshipCustomProperties,
        }
      }

      const latamLegalAddressPayload = {
        addresses: [
          {
            firstName:
              accountsContainer.profileResponse?.customer?.firstName || '',
            lastName:
              accountsContainer.profileResponse?.customer?.lastName || '',
            addressLine1:
              accountsContainer?.legalAddressFormData?.addressLine1 || '',
            addressLine2:
              accountsContainer?.legalAddressFormData?.addressLine2 || '',
            city: accountsContainer?.legalAddressFormData?.city || '',
            state: accountsContainer?.legalAddressFormData?.state || '',
            county: accountsContainer?.legalAddressFormData?.county || '',
            country: getLocaleCodeFromUrl()?.split('_')?.[1] || '',
            zip: accountsContainer?.legalAddressFormData?.zip || '',
            email: accountsContainer.profileResponse?.customer?.email || '',
            phone:
              accountsContainer.profileResponse?.customer?.phoneNumber || '',
            type: addressType,
            validated: false,
          },
        ],
      }
      const enableTaxNumberInConversion =
        convertToBoolean(
          APPConfig?.getAppConfig()?.enableTaxNumberInConversion
        ) && accountsContainer.profileResponse?.taxNumber !== ''

      if (enableTaxNumberInConversion) {
        tokenExTaxData = accountsContainer?.profileResponse?.taxNumber
        taxIdAttribute = {
          attributeId: 'taxType',
          value: 'TAX',
        }
      }
      const baseProperties = [sponsorIdAttribute]
      const postDataObj = {
        conversionType: type,
        userConsent: userConsent,
        account: {
          id: id,
          name: userName,
          typeId: accountIdConvert,
          type: profileData.accountType,
          ...(type !== 'RC_2_PM' && !enableLegalAddress()
            ? { taxNumber: tokenExTaxData || '' }
            : {}),
          phoneNumber: phone,
          ...(enableLegalAddress()
            ? latamLegalAddressPayload?.addresses[0]?.addressLine1 !== '' &&
              latamLegalAddressPayload
            : type !== 'RC_2_PM'
              ? {
                  addresses: [
                    {
                      firstName,
                      lastName,
                      addressLine1,
                      addressLine2,
                      city,
                      state,
                      country,
                      zip,
                      email,
                      phone,
                      type: addressType,
                      validated: false,
                    },
                  ],
                }
              : { addresses: [] }),
          properties:
            type !== 'RC_2_PM'
              ? [...baseProperties, taxIdAttribute, taxNumUnformatted]
              : [...baseProperties],
          customer:
            {
              ...customer,
              ...(isDobEnabled(conversionTargetType) &&
                dateOfBirth !== '' && {
                  dateOfBirth,
                }),
            } || {},
        },
        ...(isTaxIdEnabled && {
          legalTaxInfo: accountsContainer?.legalTaxLoadParam?.postData,
        }),
      }

      /** accountProperties (e.g. PecEmail) now ships inside account.properties above, not legalTaxInfo. */
      const legalTaxInfoPayload = omit(
        accountsContainer?.legalTaxLoadParam?.postData || {},
        'accountProperties'
      )
      const smsVerifyAccountProperties =
        type === 'BA_2_BE' && enableTaxNumberInConversion
          ? [...baseProperties, taxIdAttribute]
          : baseProperties
      const resolvedAccountProperties =
        type !== 'RC_2_PM' &&
        !(type === 'BA_2_BE' && enableTaxNumberInConversion)
          ? buildConversionAccountProperties(
              baseProperties,
              taxIdAttribute,
              dupCharTokenExTaxData
            )
          : smsVerifyAccountProperties

      const postDataObjSMSVerify = {
        conversionType: type,
        userConsent: userConsent,
        account: {
          id: id,
          name: userName,
          typeId: accountIdConvert,
          type: profileData.accountType,
          ...(type === 'BA_2_BE' && enableTaxNumberInConversion
            ? { taxNumber: tokenExTaxData || '' }
            : {}),
          phoneNumber:
            customerContainer?.profileResponse?.accounts?.phoneNumber,
          properties: resolvedAccountProperties,
          customer: {
            customProperties: {
              ...accertifyProps,
              ...citizenshipCustomProperties,
            },
            phoneNumber:
              customerContainer?.profileResponse?.accounts?.phoneNumber,
            ...(isDobEnabled(conversionTargetType) &&
              dateOfBirth !== '' && {
                dateOfBirth,
              }),
          },
          ...((type === 'RC_2_PM'
            ? enableLegalAddressForMemConversion()
            : enableLegalAddress()) &&
            latamLegalAddressPayload?.addresses[0]?.addressLine1 !== '' &&
            latamLegalAddressPayload),
        },
        ...(isTaxIdEnabled &&
          accountsContainer?.legalTaxLoadParam?.postData?.userTaxs?.length >
            0 && {
            legalTaxInfo: legalTaxInfoPayload,
          }),
      }

      const loadParams = {
        endPointName: 'updateAccountType',
        pathParams: `${id}/upgrade`,
        postData: enablePhoneVerificationForConversion()
          ? postDataObjSMSVerify
          : postDataObj,
      }
      const response = await this.fetchResponse(loadParams)
      trackAccountUpgrade(
        enablePhoneVerificationForConversion()
          ? postDataObjSMSVerify
          : postDataObj
      )

      const responseCode = response?.code || ''
      const isDuplicateTaxId =
        responseCode === 'EOACSU000015' || responseCode === 'NXUSER_231'

      if (isDuplicateTaxId) {
        const classElement = document?.querySelector(
          '[customclassname="tax-info"]'
        )
        classElement?.parentElement?.classList?.add('duplicate-info-exist')
        accountsContainer.duplicateTaxIdExist = true
        toastState.setToastMessage(
          i18nTranslate(
            'signup.accountCreationTaxFailure',
            'The Tax ID provided is already in use. Please provide a unique Tax ID to continue.'
          ),
          false
        )
        this.pageProfileRedirect = false
      } else if (this.isSuccessResponse(response)) {
        deleteFromSessionStorage('conv-sponsor')
        deleteFromSessionStorage('switchsponsor')
        deleteFromSessionStorage('mem')

        accountsContainer.signUpIronCladCheck = {
          clickWrap1: false,
          clickWrap2: false,
          clickWrap3: false,
        }
        accountsContainer.convertAccoutType = ''
        accountsContainer.selectedAccountType = ''
        const previousAccountType = getLocalStorage('accountType') || ''
        deleteFromLocalStorage('accountType')
        setLocalStorage('accountType', profileData.accountType)
        if (
          profileData.accountType === 'Brand Affiliate' &&
          (previousAccountType === 'Retail Customer' ||
            previousAccountType === 'Preferred Customer/Member')
        ) {
          toastState.setToastMessage(
            i18nTranslate(
              'accounts.updateAccountSuccess',
              'Please be patient while we update your account type in our systems and refrain from any business activity until you are able to see your account in V&G or Stela. This could take more than 10 minutes.'
            ),
            true,
            6000
          )
        } else {
          toastState.setToastMessage(
            i18nTranslate(
              'accounts.updateAccountSuccessGeneral',
              'Account type updated successfully'
            ),
            true,
            6000
          )
        }
        await this.getAllAccountAddress(customerContainer.accountId)
        await accountsContainer?.getProfile(customerContainer.accountId)
        await customerContainer?.getProfile()
        TagManager.dataLayer({
          dataLayer: {
            event: 'AccountTypeConversion',
            pagePath: pageNames.b2bAccountTypeConvert,
            pageTitle: 'Account Type Convert',
            previousAccountType,
            currentAccountType: response.accountType || null,
            accountid: response.id || null,
            name: response.name || null,
            platform: 'equinox',
          },
        })
        const taxInfoMsg = i18nTranslate(
          'accounts.taxInfo',
          'A Business Tax Number is required to be paid. Please visit V&G Preferences to add.'
        )
        const enableTaxRequiredToastAfterConversion = convertToBoolean(
          APPConfig?.getAppConfig()?.enableTaxRequiredToastAfterConversion
        )

        if (
          type === 'BA_2_BE' &&
          enableJITSU() &&
          !isLATAM() &&
          enableTaxRequiredToastAfterConversion
        ) {
          modalState.setModalMessage(
            taxInfoMsg,
            i18nTranslate('modaView.buttonText', 'OK'),
            (this.props = { customClassName: 'tax-info-modal-message' })
          )
        }

        this.pageProfileRedirect = true
        scrollState.scrollToTop(0)
      } else {
        const errorMessage =
          response?.message ||
          i18nTranslate(
            'accounts.updateAccountFailure',
            'Unable to update account type, please try again'
          )
        toastState.setToastMessage(errorMessage, false)
        this.pageProfileRedirect = false
      }
    } else {
      toastState.setToastMessage(
        i18nTranslate(
          'accounts.selectConsent',
          'Select terms and condition to continue.'
        ),
        false
      )
    }
  }
}

const accountsAddressContainer = new AccountsAddressContainer()

export { accountsAddressContainer }
export default accountsAddressContainer
